import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/auth-context';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Button } from '../ui/button';

/**
 * Landing Page Navigation Bar
 * 
 * Uses coal texture background and brand.svg logo
 */
export default function LandingNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  // Determine dashboard route based on user role
  const getDashboardRoute = () => {
    if (!user) return '/dashboard';
    if (user.role === 'guest') return '/my-account';
    return '/dashboard'; // admin, accountant, auditor, employee
  };

  const navLinks = [
    { label: 'ACCOMMODATION', to: '/accommodation' },
    { label: 'BAR AND GRILL', to: '/bar-and-grill' },
    { label: 'TOURS', to: '/tours' },
    { label: 'CONTACT US', to: '/contact' },
  ];

  return (
    <>
      <style>{`
        /* Ensure Sheet overlay and content are above everything */
        [data-radix-dialog-overlay],
        [data-state="open"][role="dialog"] > div:first-child {
          z-index: 9998 !important;
        }
        [data-radix-dialog-content],
        [data-state="open"][role="dialog"] > div:last-child {
          z-index: 9999 !important;
        }
        /* Target Sheet specifically */
        body > [data-radix-portal] [data-radix-dialog-overlay] {
          z-index: 9998 !important;
        }
        body > [data-radix-portal] [data-radix-dialog-content] {
          z-index: 9999 !important;
        }
      `}</style>
      <nav 
        className="relative w-full"
        style={{
          width: '100%',
          height: '80px',
          backgroundImage: 'url(/images/webflow/coal-texture.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 10,
        }}
      >
        {/* Minimal overlay - reduce or remove if texture not visible */}
        <div className="absolute inset-0 bg-black/5"></div>
        
        <div className="relative z-10 max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo - Brand SVG on Left - Smaller size */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src="/brand.svg" 
                alt="Ikoro House" 
                className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 object-contain"
              />
            </Link>

            {/* Desktop Navigation and Login Button - Right side */}
            <div className="hidden md:flex items-center ml-auto space-x-6 lg:space-x-8 flex-shrink-0">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-white transition-colors font-semibold text-sm lg:text-base uppercase tracking-wider"
                  style={{ 
                    color: 'white',
                    fontFamily: 'Montserrat, sans-serif',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#f5945c'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'white'}
                >
                  {link.label}
                </Link>
              ))}
              {/* Show Dashboard link and Logout if authenticated, Login button if not */}
              {isAuthenticated ? (
                <>
                  <Link
                    to={getDashboardRoute()}
                    className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity font-semibold uppercase text-sm text-white"
                    style={{ backgroundColor: '#f5945c', fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {user?.role === 'guest' ? 'MY ACCOUNT' : 'DASHBOARD'}
                  </Link>
                  <Button
                    onClick={handleLogout}
                    className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity font-semibold uppercase text-sm text-white border border-white/30"
                    style={{ 
                      backgroundColor: 'transparent', 
                      fontFamily: 'Montserrat, sans-serif',
                    }}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    LOGOUT
                  </Button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity font-semibold uppercase text-sm text-white"
                  style={{ backgroundColor: '#f5945c', fontFamily: 'Montserrat, sans-serif' }}
                >
                  LOGIN
                </Link>
              )}
            </div>

            {/* Mobile Menu Button - Using Sheet Component */}
            <div className="md:hidden flex-shrink-0">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20 p-2 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    <span className="sr-only">Open navigation menu</span>
                  </Button>
                </SheetTrigger>
              <SheetContent 
                side="right" 
                className="w-80 p-0 !z-[9999]" 
                style={{ zIndex: 9999 }}
              >
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f5945c' }}>
                          <img 
                            src="/brand.svg" 
                            alt="Ikoro House" 
                            className="h-8 w-8 object-contain"
                          />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-foreground">Ikoro House</h2>
                          <p className="text-xs text-muted-foreground">Navigation</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Navigation Links */}
                    <div className="space-y-2">
                      {navLinks.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block w-full text-left px-4 py-3 rounded-lg font-semibold uppercase tracking-wider transition-colors text-foreground"
                          style={{ 
                            fontFamily: 'Montserrat, sans-serif',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dfc9b1';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>

                    {/* Login/Dashboard and Logout Buttons */}
                    <div className="pt-4 border-t border-border space-y-2">
                      {isAuthenticated ? (
                        <>
                          <Link
                            to={getDashboardRoute()}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block w-full px-6 py-3 rounded-lg hover:opacity-90 transition-opacity font-semibold uppercase text-sm text-white text-center"
                            style={{ backgroundColor: '#f5945c', fontFamily: 'Montserrat, sans-serif' }}
                          >
                            {user?.role === 'guest' ? 'MY ACCOUNT' : 'DASHBOARD'}
                          </Link>
                          <Button
                            onClick={handleLogout}
                            className="w-full px-6 py-3 rounded-lg hover:opacity-90 transition-opacity font-semibold uppercase text-sm text-white border border-gray-300"
                            style={{ 
                              backgroundColor: 'transparent', 
                              color: '#2C2416',
                              fontFamily: 'Montserrat, sans-serif',
                            }}
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            LOGOUT
                          </Button>
                        </>
                      ) : (
                        <Link
                          to="/login"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block w-full px-6 py-3 rounded-lg hover:opacity-90 transition-opacity font-semibold uppercase text-sm text-white text-center"
                          style={{ backgroundColor: '#f5945c', fontFamily: 'Montserrat, sans-serif' }}
                        >
                          LOGIN
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}